import { useEffect, useState } from 'react';
import {
  useCaptureSettingsQuery, useConnectionsQuery, useMeQuery,
  useSaveCaptureSettingsMutation,
  type CaptureSettingsValue,
} from './api.js';
import { CaptureLabels } from './CaptureLabels.js';
import { Alert, Button, Card, SectionHeading, Spinner } from './ui.js';

/**
 * Email → CRM capture: which emails ContactPilot may read, and what it does with them.
 *
 * The product no longer reads a whole mailbox. The customer marks an email with a Gmail
 * label or an Outlook category they configured, and only that email is retrieved and
 * analysed. This screen is where those labels are configured, so it is also where that boundary has to be
 * said in plain words — a customer who believes "AI reads my inbox" is a customer who was
 * told something false.
 *
 * The CRM tags an email's contacts start with come from its label, and nowhere else: the
 * workspace-wide email default was retired, so there is one place to look and Review is
 * where each contact's tags are finally decided.
 */

const isAdminRole = (role: string | null | undefined) => role === 'owner' || role === 'admin';

/** Server errors, as sentences. */
function errorText(e: unknown): string {
  const status = (e as { status?: number })?.status;
  if (status === 403) return 'Only a workspace owner or admin can change this.';
  return 'That could not be saved. Try again.';
}

/** "acme.com, Acme.co.uk\nexample.org" → ['acme.com', 'acme.co.uk', 'example.org']. */
function parseDomains(raw: string): string[] {
  const seen = new Set<string>();
  for (const part of raw.split(/[\s,;]+/)) {
    const d = part.trim().toLowerCase().replace(/^@/, '').replace(/^https?:\/\//, '').replace(/\/.*$/, '');
    if (d) seen.add(d);
  }
  return [...seen];
}

const sameList = (a: readonly string[], b: readonly string[]) =>
  a.length === b.length && a.every((x) => b.includes(x));

/*
 * The two windows the privacy panel describes, named rather than typed into sentences.
 *
 * They are independent clocks and customers read them as one. Ninety days is how long
 * message CONTENT survives, counted from when the message arrived. Thirty days is how
 * long a DISCONNECTED mailbox's data survives, counted from the disconnect. Neither
 * extends the other, and the copy says so because two numbers on one page read as a sum.
 */
const RETENTION_DAYS = 90;
const GRACE_DAYS = 30;

/**
 * The fixed data-handling facts, moved here from the retired Retention & Privacy tab.
 *
 * Fixed policy is not a setting, so it is not a tab; it is a disclosure next to the
 * setting it describes. LEGAL REVIEW REQUIRED on any change — see
 * docs/email-ai-disclosures.md (D2, D3, D5). No zero-retention claim: the provider's own
 * retention is not ours to promise.
 */
function PrivacyPanel({ defaultOpen }: { defaultOpen: boolean }) {
  return (
    <details
      className="min-w-0 rounded-lg border border-slate-200 bg-white"
      open={defaultOpen}
      data-privacy-panel=""
    >
      <summary className="flex min-h-11 cursor-pointer items-center px-4 text-sm font-semibold text-slate-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900">
        Privacy &amp; data
      </summary>
      <div className="space-y-4 border-t border-slate-200 px-4 py-4 text-sm text-slate-700">
        <section data-what-we-read="">
          <h4 className="font-semibold text-slate-900">What ContactPilot reads</h4>
          <p className="mt-1">
            Only emails you mark with a capture label or category. From a marked email it reads
            the sender, the people in To and CC (when that is turned on), signature details and
            company information, and writes a short intent and summary. Details can also come
            from earlier messages quoted inside that email.
          </p>
          <p className="mt-2">
            Their content is sent to our AI provider, Anthropic, to find the people in them,
            their contact details, why they wrote and a short summary.
            The content of unmarked mail is not downloaded or sent to an AI provider.
            Attachment files are never downloaded or sent.
          </p>
        </section>
        <ul className="space-y-3">
          <li>
            <strong className="text-slate-900">Your mailbox is not changed.</strong> Nothing in
            your mailbox is sent, deleted, moved or relabelled, and ContactPilot never applies or
            removes labels or categories on your messages. If you allow label or category
            creation, it only creates the ones you configure here.
          </li>
          <li>
            <strong className="text-slate-900">Marked email text.</strong> Kept for up to{' '}
            {RETENTION_DAYS} days, then removed. What remains is a hash, the participants and
            the dates — enough to recognise a message, not enough to re-read it.
          </li>
          <li>
            <strong className="text-slate-900">Disconnecting a mailbox.</strong> Stops reading
            and removes the stored sign-in immediately. What was already read is kept for{' '}
            {GRACE_DAYS} days so you can reconnect, then deleted — unless you choose to delete
            it now when you disconnect. The two windows do not add up: the {RETENTION_DAYS}-day
            content limit keeps running while a mailbox is disconnected.
          </li>
          <li>
            <strong className="text-slate-900">Card and list photos.</strong> Deleted as soon as
            the reading finishes, successfully or not.
          </li>
          <li>
            <strong className="text-slate-900">Your CRM.</strong> When you approve a capture, a
            short summary of the marked email may be written to your CRM as a note on the contact.
          </li>
          <li>
            <strong className="text-slate-900">Model training.</strong> Anthropic acts as a
            processor on our instructions and, under its commercial terms, may not train
            models on this content.
          </li>
        </ul>
      </div>
    </details>
  );
}

/** The three fields a save sends — never the retired defaults a server response may carry. */
const pick = (v: CaptureSettingsValue): CaptureSettingsValue => ({
  mode: v.mode, includeRecipients: v.includeRecipients, internalDomains: [...v.internalDomains],
});

export function CaptureSettings({ onOpenConnections, onOpenCrmTags, onOpenImageFile, privacyOpen = false }: {
  onOpenConnections?: () => void;
  onOpenCrmTags?: () => void;
  /** Image & File Capture, where the phone number region lives. */
  onOpenImageFile?: () => void;
  /** Arrived from the old Retention & Privacy link: open the disclosure. */
  privacyOpen?: boolean;
}) {
  const me = useMeQuery();
  const { data, isLoading, error } = useCaptureSettingsQuery();
  const connections = useConnectionsQuery();
  const [save, { isLoading: saving }] = useSaveCaptureSettingsMutation();
  const [draft, setDraft] = useState<CaptureSettingsValue | null>(null);
  const [domainsText, setDomainsText] = useState('');
  /*
   * Auto is confirmed explicitly, every time it is newly chosen.
   *
   * A radio that silently turned on automatic CRM writes would be one mis-tap away from
   * contacts appearing in somebody's CRM unreviewed. The warning names what is actually
   * at stake — the marked emails, never "your inbox" — and Save stays disabled until it
   * is acknowledged.
   */
  const [autoConfirmed, setAutoConfirmed] = useState(false);
  const [result, setResult] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);

  const canEdit = isAdminRole(me.data?.role);

  /*
   * Re-seeded only when the SAVED settings change. Changing a capture label refetches this query
   * too, and resetting on every refetch would throw away unsaved edits further down.
   */
  const savedKey = data ? JSON.stringify(pick(data.settings)) : null;
  useEffect(() => {
    if (!data) return;
    setDraft(pick(data.settings));
    setDomainsText(data.settings.internalDomains.join('\n'));
    setAutoConfirmed(false);
  }, [savedKey]);

  if (isLoading) return <Spinner label="Loading email capture settings…" />;
  if (error || !data) return <Alert tone="error">Email capture settings could not be loaded.</Alert>;
  if (!draft) return <Spinner label="Loading email capture settings…" />;

  const saved = data.settings;
  const nouns = data.mailbox?.provider === 'microsoft' ? 'categories' : 'labels';
  /*
   * The one AI state a customer can act on: no key. Model ids, rollout and server
   * switches are not the customer's to see or change, so they are not shown.
   */
  const aiKey = connections.data?.connectors.find((c) => c.kind === 'anthropic') ?? null;
  const noAiKey = aiKey !== null && (aiKey.status === 'NOT_CONNECTED' || aiKey.status === 'REAUTH_REQUIRED');
  const domains = parseDomains(domainsText);
  const needsAutoConfirm = draft.mode === 'auto' && saved.mode !== 'auto' && !autoConfirmed;
  const dirty = draft.mode !== saved.mode
    || draft.includeRecipients !== saved.includeRecipients
    || !sameList(domains, saved.internalDomains);

  const set = (patch: Partial<CaptureSettingsValue>) => {
    setResult(null);
    setDraft((d) => (d ? { ...d, ...patch } : d));
  };

  const onSave = async () => {
    setResult(null);
    try {
      await save({ mode: draft.mode, includeRecipients: draft.includeRecipients, internalDomains: domains }).unwrap();
      setResult({ tone: 'success', text: 'Email capture settings saved.' });
    } catch (e) {
      setResult({ tone: 'error', text: errorText(e) });
    }
  };

  return (
    <div className="min-w-0 space-y-6">
      <SectionHeading
        title="Email Capture"
        hint="Mark an email in Gmail or Outlook and ContactPilot prepares the contacts for your CRM."
      />

      {noAiKey ? (
        <Alert tone="error">
          <span className="block">
            AI analysis unavailable — add an Anthropic key in Connections. Marked emails
            cannot be read until there is one.
          </span>
          {onOpenConnections ? (
            <button
              type="button" onClick={onOpenConnections}
              className="mt-1 inline-flex min-h-11 items-center font-medium underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
            >
              Go to Connections
            </button>
          ) : null}
        </Alert>
      ) : null}
      {!data.crmWriteEnabled ? (
        <Alert tone="info">
          CRM sending is off for this workspace. Approvals are recorded, and nothing is sent
          to your CRM.
        </Alert>
      ) : null}

      {!canEdit ? (
        <Alert tone="info">
          Only a workspace owner or admin can change these settings. You can see them here.
        </Alert>
      ) : null}

      <Card className="p-5">
        <h3 className="text-base font-semibold text-slate-900">
          Capture {nouns}
        </h3>
        <div className="mt-3">
          <CaptureLabels canEdit={canEdit} onOpenConnections={onOpenConnections} onManageTags={onOpenCrmTags} />
        </div>
      </Card>

      <fieldset disabled={!canEdit || saving} className="min-w-0">
        <Card className="p-5">
          <h3 className="text-base font-semibold text-slate-900" id="after-mark-heading">After I mark an email</h3>
          <div className="mt-3 space-y-2" role="radiogroup" aria-labelledby="after-mark-heading">
            <label className="flex min-h-11 items-start gap-3 py-1">
              <input
                type="radio" name="capture-mode" value="review"
                checked={draft.mode === 'review'}
                onChange={() => { set({ mode: 'review' }); setAutoConfirmed(false); }}
                className="mt-0.5 h-5 w-5 shrink-0"
              />
              <span className="text-sm">
                <span className="block font-medium text-slate-900">
                  Review before sending <span className="font-normal text-slate-500">(recommended)</span>
                </span>
                <span className="block text-slate-600">
                  Review the contacts and tags before anything is sent.
                </span>
              </span>
            </label>
            <label className="flex min-h-11 items-start gap-3 py-1">
              <input
                type="radio" name="capture-mode" value="auto"
                checked={draft.mode === 'auto'}
                onChange={() => set({ mode: 'auto' })}
                className="mt-0.5 h-5 w-5 shrink-0"
              />
              <span className="text-sm">
                <span className="block font-medium text-slate-900">Automatically send safe contacts</span>
                <span className="block text-slate-600">
                  Safe contacts can be sent automatically. Anything uncertain goes to Review.
                </span>
              </span>
            </label>
          </div>
          {draft.mode === 'auto' && saved.mode !== 'auto' ? (
            <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" role="alert">
              <p className="[overflow-wrap:anywhere]">
                Emails you mark with the {nouns} configured here
                may create/update CRM records automatically when identity checks pass. Ambiguous cases will still go to
                Review.
              </p>
              {autoConfirmed ? (
                <p className="mt-2 font-medium">Confirmed. Save to turn it on.</p>
              ) : (
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button className="min-h-11" onClick={() => setAutoConfirmed(true)}>
                    Confirm automatic sending
                  </Button>
                  <Button
                    variant="secondary" className="min-h-11"
                    onClick={() => { set({ mode: 'review' }); setAutoConfirmed(false); }}
                  >
                    Keep reviewing
                  </Button>
                </div>
              )}
            </div>
          ) : null}
        </Card>
      </fieldset>

      {/*
        Everything with a sensible default, behind one disclosure: whether To and CC people
        are captured too, which domains are colleagues, and the privacy facts. An old
        privacy link opens it with Privacy & data showing.
      */}
      <details
        className="min-w-0 rounded-lg border border-slate-200 bg-white shadow-sm"
        open={privacyOpen}
        data-advanced-settings=""
      >
        <summary className="flex min-h-11 cursor-pointer items-center px-5 text-base font-semibold text-slate-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900">
          Advanced
        </summary>
        <div className="min-w-0 space-y-5 border-t border-slate-200 p-4 sm:p-5">
          <fieldset disabled={!canEdit || saving} className="min-w-0 space-y-5">
            <label className="flex min-h-11 min-w-0 items-start justify-between gap-4" data-include-recipients="">
              <span className="min-w-0 text-sm">
                <span className="block font-medium text-slate-900">Also capture people in To and CC</span>
                <span className="block text-slate-600">
                  Other people on a marked email who are not your colleagues.
                </span>
              </span>
              <input
                type="checkbox" role="switch"
                checked={draft.includeRecipients}
                aria-checked={draft.includeRecipients}
                onChange={(e) => set({ includeRecipients: e.target.checked })}
                className="peer sr-only"
              />
              <span
                aria-hidden="true"
                className="relative mt-0.5 inline-flex h-6 w-11 shrink-0 items-center rounded-full bg-slate-300 transition peer-checked:bg-slate-900 peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-slate-900 peer-disabled:opacity-50 after:absolute after:left-0.5 after:h-5 after:w-5 after:rounded-full after:bg-white after:shadow after:transition peer-checked:after:translate-x-5"
              />
            </label>

            <div className="min-w-0">
              <label className="block text-sm" htmlFor="capture-internal-domains">
                <span className="font-medium text-slate-900">Internal domains</span>
                <span className="mt-0.5 block text-slate-600">
                  Your own company domains, so colleagues are never proposed as contacts.
                  Optional — one per line, or separated by commas.
                </span>
              </label>
              <textarea
                id="capture-internal-domains"
                value={domainsText}
                rows={3}
                onChange={(e) => { setResult(null); setDomainsText(e.target.value); }}
                placeholder={'yourcompany.com\nyourcompany.co.uk'}
                className="mt-2 w-full max-w-md rounded-md border border-slate-300 px-3 py-2 font-mono text-sm"
              />
            </div>
          </fieldset>
          <PrivacyPanel defaultOpen={privacyOpen} />
          <p className="text-sm text-slate-600">
            Phone number region is set in{' '}
            {onOpenImageFile ? (
              <button
                type="button" onClick={onOpenImageFile}
                className="inline-flex min-h-11 items-center font-medium text-slate-900 underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
              >
                Image &amp; File Capture
              </button>
            ) : 'Image & File Capture'}.
          </p>
        </div>
      </details>

      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <Button
            className="min-h-11"
            disabled={!canEdit || saving || !dirty || needsAutoConfirm}
            onClick={() => void onSave()}
          >
            {saving ? 'Saving…' : 'Save settings'}
          </Button>
          {needsAutoConfirm ? (
            <span className="text-sm text-amber-800">Confirm automatic sending above to save.</span>
          ) : null}
        </div>
        <div aria-live="polite">
          {result ? <Alert tone={result.tone}>{result.text}</Alert> : null}
        </div>
      </div>
    </div>
  );
}
